<template>
  <!-- The five-tab shell (brief §2; re-ordered 2026-09-14; 챌린지 2026-10):
       챌린지 · 데일리 루틴 · 소통방 · 수업 · 표현집. 챌린지 is the first tab and
       where a member lands; 홈 is renamed 데일리 루틴 and its house became a
       sprout, a little growth every day (the page and its address stay); 프로필
       left the bar for the avatar, top right on every top-level page on a
       phone and beside the level pill here on desktop, so the bar stays at
       five. 소통방 carries the unread count. One component,
       two form factors — a fixed bottom bar under 768px, a fixed top nav above.
       `active` is a prop, not route-matching: until the F flip, only the
       engine surfaces render this bar, and each names its own tab. Host views
       must pad for it: var(--etb-pad-bottom) on mobile, --etb-pad-top on
       desktop (both defined here on the wrapper's ancestors via :root-less
       custom props is overkill — views just use the documented constants:
       64px + safe-area bottom, 60px top). An account in the new 챌린지
       program gets a sixth, 새 챌린지, first (programStore). -->
  <nav class="etb" :class="{ 'etb--desk-only': deskOnly, 'etb--six': tabs.length > 5 }" aria-label="주요 메뉴">
    <div class="etb__inner">
      <router-link class="etb__brand" :to="enginePath('/challenges')" aria-label="YoungPro 챌린지">
        <img class="etb__logo" src="/assets/logo-text-only.png" alt="youngpro" width="2521" height="470" />
      </router-link>
      <div class="etb__tabs">
        <router-link
          v-for="t in tabs"
          :key="t.key"
          class="etb__tab"
          :data-tab="t.key"
          :class="{ 'etb__tab--on': active === t.key }"
          :to="t.to"
          :aria-current="active === t.key ? 'page' : null"
        >
          <span class="etb__icw">
            <v-icon class="etb__ic" size="24">{{ active === t.key ? t.iconOn : t.icon }}</v-icon>
            <span v-if="t.key === 'community' && roomCount" class="etb__badge" :aria-label="`새 소식 ${roomCount}`">{{ roomCount > 99 ? '99+' : roomCount }}</span>
          </span>
          <span class="etb__label">{{ t.label }}</span>
        </router-link>
      </div>
      <!-- the rank pill (G2): desktop's top nav carries it; on the phone it sits beside the home greeting -->
      <span class="etb__right">
        <EngineRankPill class="etb__rank" />
        <!-- 프로필: desktop only here; on a phone each top-level page carries it -->
        <EngineAvatarLink class="etb__me" :active="active === 'more'" />
      </span>
    </div>
  </nav>
</template>

<script>
// Tier-0 (CLASS, content-only) members only have 표현집, so the bar offers
// 표현집 (프로필 is the avatar now) and nothing they cannot enter. In practice they never reach
// this component at all — router/index.js funnels tier 0 to /books for every
// route inside the requiresPayment block, and all engine routes are inside it
// (checked in the pre-merge audit, 2026-09-17). The branch stays as the
// cheap belt to that guard's braces; the tab it named was renamed 프로필 on
// 2026-09-14 and the comment had not followed.
import { waitForAuth } from '@/utils/authWait';
import userService from '@/api/userService';
import { DEPLOYMENT_LAYER } from '@/config/engineFlags';
import notificationsService from '@/api/notificationsService';
import { notifStore } from '@/services/notifStore';
import roomActivityService from '@/api/roomActivityService';
import { roomStore } from '@/services/roomStore';
import EngineRankPill from '@/components/engine/EngineRankPill.vue';
import EngineAvatarLink from '@/components/engine/EngineAvatarLink.vue';
import { enginePath } from '@/utils/engineNav';
import { programIn, loadProgram } from '@/services/programStore';

// the new 챌린지 program's tab, first in the bar for its accounts. The key is
// its own: the live tour targets challenges, community, lessons and library.
const TODAY = { key: 'today', label: '새 챌린지', to: '/today', icon: 'mdi-calendar-today-outline', iconOn: 'mdi-calendar-today' };

// how long after a page has finished loading the 챌린지 and 데일리 루틴 tabs' code is fetched (see warmTabs)
const LATE_WARM_MS = 4000;

export default {
  name: 'EngineTabBar',
  components: { EngineRankPill, EngineAvatarLink },
  props: {
    active: { type: String, default: 'home' },
    // one challenge is a focused page on a phone: its own back button and a
    // sticky CTA, no tab bar (the desktop nav stays)
    deskOnly: { type: Boolean, default: false },
  },
  computed: {
    tabs() {
      const all = programIn() ? [TODAY, ...this.allTabs] : this.allTabs;
      if (this.tier === '0') return all.filter((t) => t.key === 'library');
      return all;
    },
    /** What is waiting for me in 소통방: my unread notifications plus what has
     *  been posted since this device last looked (Eddie 2026-09-16 — a post
     *  two hours old and the tab said nothing). Standing ON the tab is looking
     *  at it, so the badge never counts against the member reading it. */
    roomCount() {
      if (this.active === 'community') return 0;
      return this.notif.unread + this.room.fresh;
    },
  },
  async mounted() {
    try {
      const user = await waitForAuth();
      // Both subscriptions are assigned AFTER these awaits, and beforeUnmount
      // runs synchronously — so a bar unmounted mid-resolution (a fast tab
      // change, a logout) used to leave two live onSnapshot listeners with
      // nothing left to cancel them. Checked after every await (audit
      // 2026-09-17).
      if (this._dead) return;
      if (user && user.uid) {
        loadProgram(user.uid); // five tabs until it answers (once per page load)
        const info = await userService.getUser(user.uid);
        if (this._dead) return;
        this.tier = (info && info.tier) || null;
        // the unread count lives with the layer (the review event is its first source)
        if (DEPLOYMENT_LAYER) this._offNotif = notificationsService.watchUnread(user.uid);
        // new posts in the room, flag or no flag — the room is live today
        this._offRoom = roomActivityService.watchFresh(user.uid);
        if (this._dead) { this.stopWatching(); return; }
        this.warmTabs();
      }
    } catch (e) { /* default: full bar */ }
  },
  beforeUnmount() {
    this._dead = true;
    this.stopWatching();
  },
  methods: {
    enginePath,
    stopWatching() {
      if (this._offNotif) { this._offNotif(); this._offNotif = null; }
      if (this._offRoom) { this._offRoom(); this._offRoom = null; }
    },
    /** Pull the other tabs' chunks once we know a member is here.
     *
     *  These five used to carry webpackPrefetch, which webpack emits in the
     *  entry chunk — so 887 KB of member-only code downloaded on the public
     *  landing page before anyone signed in. Warming from here keeps the
     *  win (a first tap never waits on a download) and spends nobody else's
     *  bandwidth. requestIdleCallback so it never competes with first paint.
     *
     *  Two of these wait longer than the others. 챌린지 is new, and 데일리 루틴
     *  was never warmed while it was the page everyone landed on: both are
     *  weight the older pages did not carry. On a cold load on a slow phone
     *  the idle callback comes while the page in front is still fetching what
     *  it shows (measured 2026-10-05: 65 KB sharing the line took 0.3 s off
     *  /home's main paint). So their code is asked for only once this page has
     *  finished loading and a few seconds have passed. On the tab itself the
     *  route has loaded it already and the import is a no-op. */
    warmTabs() {
      const idle = (fn) => { if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 4000 }); else setTimeout(fn, 1500); };
      const late = () => {
        if (this._dead) return;
        import(/* webpackChunkName: "tab-challenges" */ '@/views/ChallengesView.vue').catch(() => {});
        import(/* webpackChunkName: "tab-home" */ '@/views/EngineHomeView.vue').catch(() => {});
      };
      const settled = () => setTimeout(() => idle(late), LATE_WARM_MS);
      if (document.readyState === 'complete') settled();
      else window.addEventListener('load', settled, { once: true });
      const go = () => {
        if (this._dead) return;
        import(/* webpackChunkName: "tab-community" */ '@/views/CommunityPage.vue').catch(() => {});
        import(/* webpackChunkName: "tab-lessons" */ '@/views/EngineLessonsView.vue').catch(() => {});
        import(/* webpackChunkName: "tab-library" */ '@/views/LibraryPage.vue').catch(() => {});
        import(/* webpackChunkName: "tab-more" */ '@/views/EngineMoreView.vue').catch(() => {});
      };
      idle(go);
    },
  },
  data() {
    return {
      // paths through enginePath: the original spelling once the flag is on,
      // the -next alias before it, and never a -next literal by hand
      allTabs: [
        { key: 'challenges', label: '챌린지', to: enginePath('/challenges'), icon: 'mdi-flag-outline', iconOn: 'mdi-flag' },
        { key: 'home', label: '데일리 루틴', to: enginePath('/home'), icon: 'mdi-sprout-outline', iconOn: 'mdi-sprout' },
        { key: 'community', label: '소통방', to: enginePath('/community'), icon: 'mdi-forum-outline', iconOn: 'mdi-forum' },
        { key: 'lessons', label: '수업', to: '/lessons', icon: 'mdi-play-box-outline', iconOn: 'mdi-play-box' },
        { key: 'library', label: '표현집', to: enginePath('/library'), icon: 'mdi-book-open-outline', iconOn: 'mdi-book-open-variant' },
      ],
      tier: null,
      notif: notifStore,
      room: roomStore,
    };
  },
};
</script>

<style scoped>
/* ── Mobile: bottom bar ──────────────────────────────────────────────────── */
.etb {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 60;
  background: var(--surface);
  border-top: 1px solid var(--hairline);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
.etb__inner { display: flex; align-items: center; }
.etb__brand { display: none; }
.etb__right { display: none; }
.etb__tabs { display: flex; flex: 1; }
.etb__tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 0 6px;
  text-decoration: none;
  color: var(--text-tertiary);
  -webkit-tap-highlight-color: transparent;
  transition: transform 140ms var(--ease-press);
}
.etb__tab:active { transform: scale(0.94); }
.etb__ic { color: inherit; }
.etb__icw { position: relative; display: inline-grid; place-items: center; }
/* the count: the tab-icon idiom; orange is the app's "mine to see", never red */
.etb__badge {
  position: absolute; top: -5px; left: calc(50% + 7px);
  min-width: 17px; height: 17px; padding: 0 5px; border-radius: 999px;
  background: var(--scene, #F0501E); color: #fff;
  font-size: 10.5px; font-weight: 800; line-height: 17px; text-align: center; letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums; box-shadow: 0 0 0 2px var(--surface);
}
.etb__label { font-size: 11px; font-weight: 700; letter-spacing: -0.01em; }
.etb__tab--on { color: var(--text-primary); } /* navigation is not a task (2026-09-08) */

@media (max-width: 767.98px) {
  .etb--desk-only { display: none; }
}

/* ── Desktop: top nav ────────────────────────────────────────────────────── */
@media (min-width: 768px) {
  .etb {
    top: 0;
    bottom: auto;
    border-top: 0;
    border-bottom: 1px solid var(--hairline);
    padding-bottom: 0;
  }
  .etb__inner {
    max-width: 960px;
    margin: 0 auto;
    height: 60px;
    padding: 0 20px;
    display: flex;
    justify-content: space-between;
  }
  .etb__brand { display: block; text-decoration: none; line-height: 0; }
  .etb__logo { height: 20px; width: auto; display: block; }
  /* Equal columns, not content-width tabs (Eddie 2026-09-17). Sized to their
     own labels the five came out [71, 95, 83, 95, 95] — 홈 is one syllable and
     수업 two, so the gaps between the WORDS varied by 11px even though the
     gaps between the boxes were identical. One grid row of 1fr columns makes
     every tab the width of the widest, so the pitch is uniform by
     construction at any desktop width. The mobile bar is untouched: it is
     outside this query and already flex:1 per tab. */
  .etb__tabs { flex: none; display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 4px; }
  .etb__right { display: inline-flex; align-items: center; gap: 12px; }
  .etb__rank { display: inline-flex; }
  .etb__tab {
    flex: none;
    flex-direction: row;
    justify-content: center;
    gap: 7px;
    padding: 8px 14px;
    border-radius: 10px;
    height: 40px;
    align-self: center;
    font-size: 14px;
    color: var(--text-secondary);
    transition: background 140ms ease, transform 140ms var(--ease-press);
  }
  .etb__tab:hover { background: var(--surface-2); }
  .etb__tab:active { transform: scale(0.97); }
  .etb__ic { font-size: 20px !important; }
  .etb__label { font-size: 14px; font-weight: 700; }
  .etb__tab--on { color: var(--text-primary); background: var(--surface-3); }
  .etb__tab--on .etb__ic { color: var(--text-primary); }
}

/* A tablet-width nav. 데일리 루틴 is the widest label since 2026-10, and five
   equal columns of that width, each with its icon, no longer fit under 960px:
   the level pill and the avatar were pushed off the right edge at 768. The
   columns stay exactly equal (ruled 2026-09-17); under 960px the tabs drop
   their icons and read as labels, and the unread count moves to the tab's
   corner so 소통방 still carries it. */
@media (min-width: 768px) and (max-width: 959.98px) {
  .etb__inner { padding: 0 16px; }
  .etb__tabs { gap: 2px; }
  .etb__tab { position: relative; padding: 8px 10px; }
  .etb__ic { display: none; }
  .etb__icw { position: static; }
  .etb__badge { top: 1px; left: auto; right: 1px; }
  .etb__right { gap: 8px; }
}

/* Six items: an account in the new 챌린지 program. Six columns with their
   icons push the avatar off screen at 768 and 1024px, so at every desktop
   width the six read as labels, with 8px tab padding and 2px gaps (measured
   to fit 768 to 1280, discovery 01 F5). The unread count follows 소통방 as a
   superscript: the empty icon box moves after the word and carries it, clear
   of the label at 3, 12 and 99+. The five above stay exactly as they are; the
   phone bar takes six at its live sizes, each label on one line even in the
   wider fallback face a phone shows before Pretendard arrives. */
@media (max-width: 767.98px) {
  .etb--six .etb__label { white-space: nowrap; }
}
@media (min-width: 768px) {
  .etb--six .etb__inner { padding: 0 16px; }
  .etb--six .etb__tabs { gap: 2px; }
  .etb--six .etb__tab { position: relative; gap: 0; padding: 8px; }
  .etb--six .etb__ic { display: none; }
  .etb--six .etb__icw { position: relative; order: 1; }
  .etb--six .etb__badge { top: -16px; left: 3px; right: auto; }
}

@media (prefers-reduced-motion: reduce) {
  .etb__tab { transition: none; }
}
</style>
